import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { getMe } from "../api/user";
import { stopCastKeepalive } from "../api/cast";
import { castKeyOf, isCastLocalId } from "../lib/castDock";
import { timelineOf } from "../lib/castSync/timeline";
import { ownCastRecord } from "../lib/ownCastRecord";
import { slotEntries } from "../lib/streamSlot";
import { useAuthStore } from "../store/authStore";
import { resetCastDockStore } from "../store/castDockStore";
import { onOwnCastStopped, useOwnCastStore } from "../store/ownCastStore";
import { findSlot, onSlotsLoaded, useStreamStatusStore } from "../store/streamStatusStore";
import type { StreamSlot, StreetChatStreamInfo } from "../types/stream";

/** The slot list shows this very own cast (busy, same cast identity). */
const showsCast = (slot: StreamSlot | undefined, key: string): boolean =>
  slot != null && slot.available === false && castKeyOf(slot) === key;

/** Android `reconcileOwnCasts`: one slot load's file/link slots against this tab's record. */
function reconcileInfo(gcId: number, info: StreetChatStreamInfo | undefined, me: string) {
  const loaded = slotEntries(info)
    .filter((e) => isCastLocalId(e.localId))
    .map((e) => e.slot);
  useOwnCastStore.getState().reconcile(loaded, me, gcId);
}

/**
 * Your own casts' lifecycle around the stores (called once, in StreamDock):
 * - a closing tab (`pagehide`) sends each own cast's stop with keepalive;
 * - logout stops every own cast once and empties the own-cast store, the dock store and the
 *   tab's orphan record;
 * - the slot list ending an own cast (its slot freed, or another cast in it) ends it here with
 *   no stop call (Android's STOP_STREAM `onServerStopped`); else its newer timeline is offered;
 * - every full slot load reconciles this tab's orphan record (Android `reconcileOwnCasts`);
 * - a stop the server took refreshes that cast's conversation.
 */
export function useOwnCastLifecycle(): void {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated());
  const { data: me } = useQuery({ queryKey: ["me"], queryFn: getMe, enabled: isAuthenticated });
  const meId = isAuthenticated ? (me?.id ?? null) : null;
  const meRef = useRef<string | null>(meId);
  meRef.current = meId;
  const wasAuthenticated = useRef(isAuthenticated);

  // Keyed on the auth transition, as PipHost's reset is.
  useEffect(() => {
    if (wasAuthenticated.current && !isAuthenticated) {
      useOwnCastStore.getState().stopAll();
      ownCastRecord.clear();
      resetCastDockStore();
    }
    wasAuthenticated.current = isAuthenticated;
  }, [isAuthenticated]);

  useEffect(() => {
    const onPageHide = () => {
      for (const cast of Object.values(useOwnCastStore.getState().casts)) {
        if (cast) stopCastKeepalive(cast.slot.id);
      }
    };
    window.addEventListener("pagehide", onPageHide);
    return () => window.removeEventListener("pagehide", onPageHide);
  }, []);

  useEffect(
    () =>
      useStreamStatusStore.subscribe((state, prev) => {
        if (state.infoByGc === prev.infoByGc) return;
        const { casts, onServerStopped, onTimeline } = useOwnCastStore.getState();
        for (const cast of Object.values(casts)) {
          if (!cast) continue;
          const slotId = cast.slot.id;
          const now = findSlot(state.infoByGc, slotId)?.slot;
          if (!now) continue;
          const key = castKeyOf(cast.slot);
          if (showsCast(now, key)) {
            const t = timelineOf(now);
            if (t) onTimeline(slotId, t);
            continue;
          }
          // Only a list that showed this cast and now does not ended it: a copy cached before
          // the publish (still free, or the previous cast in the slot) must not end a new cast.
          if (showsCast(findSlot(prev.infoByGc, slotId)?.slot, key)) onServerStopped(slotId);
        }
      }),
    [],
  );

  useEffect(
    () =>
      onSlotsLoaded((gcId, info) => {
        const me = meRef.current;
        if (me != null) reconcileInfo(gcId, info, me);
      }),
    [],
  );

  // A load that landed before `me` was known: reconcile what is cached once it is.
  useEffect(() => {
    if (meId == null) return;
    const { infoByGc } = useStreamStatusStore.getState();
    for (const key of Object.keys(infoByGc)) reconcileInfo(Number(key), infoByGc[Number(key)], meId);
  }, [meId]);

  useEffect(
    () =>
      onOwnCastStopped((_slotId, gcId) => {
        if (!useAuthStore.getState().isAuthenticated()) return;
        const stc = useStreamStatusStore.getState().stcId;
        if (stc != null) void useStreamStatusStore.getState().refresh(stc, gcId);
      }),
    [],
  );
}
